import React, { forwardRef, useId } from 'react';
import { ChevronDown } from 'lucide-react';

interface Option {
  value: string;
  label: string;
}

interface SelectFieldProps extends React.SelectHTMLAttributes<HTMLSelectElement> {
  label: string;
  options: Option[];
  error?: string;
  helperText?: string;
  placeholder?: string;
}

export const SelectField = forwardRef<HTMLSelectElement, SelectFieldProps>(
  ({ label, options, error, helperText, placeholder, className = '', ...props }, ref) => {
    const generatedId = useId();
    const id = props.id || generatedId;
    const selectClasses = `input-field appearance-none pr-10 cursor-pointer ${error ? 'input-error' : ''} ${className}`;

    return (
      <div className="field-group">
        <label htmlFor={id} className="form-label">
          {label}
          {props.required && <span className="form-label-required">*</span>}
        </label>

        <div className="relative">
          <select
            ref={ref}
            id={id}
            className={`${selectClasses} focus-visible`}
            aria-invalid={!!error}
            aria-describedby={error ? `${id}-error` : undefined}
            {...props}
          >
            {placeholder && (
              <option value="" disabled>
                {placeholder}
              </option>
            )}
            {options.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>

          <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400 pointer-events-none transition-colors" />
        </div>

        {error && <p id={`${id}-error`} className="error-message">{error}</p>}

        {!error && helperText && <p className="helper-text">{helperText}</p>}
      </div>
    );
  }
);

SelectField.displayName = 'SelectField';